// src/utils/session.ts
// ─────────────────────────────────────────────────────────────────────────────
// Save / load client session files.
// Uses the File System Access API (showSaveFilePicker) so that repeated saves
// overwrite the same file rather than downloading a new one each time.
// ─────────────────────────────────────────────────────────────────────────────

import React from 'react';
import { ClientData, BLANK_CLIENT } from '../types/ClientData';
import { getTaxYearForDate } from '../taxConfig';

const SCHEMA_VERSION = 1;

export interface SessionFile {
  schemaVersion: number;
  savedAt: string;
  data: ClientData;
}

// ── Suggested filename helper ────────────────────────────────────────────────

function suggestedFilename(data: ClientData): string {
  const clientSlug = data.clientName.replace(/[^a-zA-Z0-9]/g, '_').replace(/_+/g, '_') || 'Client';
  const dateSlug = new Date().toLocaleDateString('en-GB').replace(/\//g, '');
  const sessionNum = data.sessionType === 'new' ? 'S1' : data.sessionType === 's2' ? 'S2' : data.sessionType === 's3' ? 'S3' : 'AR';
  return `FFM_${clientSlug}_${sessionNum}_${dateSlug}.json`;
}

// ── Build JSON payload ───────────────────────────────────────────────────────

function buildPayload(data: ClientData): string {
  const file: SessionFile = {
    schemaVersion: SCHEMA_VERSION,
    savedAt: new Date().toISOString(),
    data,
  };
  return JSON.stringify(file, null, 2);
}

// ── Fallback: classic <a> download (for browsers without File System Access) ─

function downloadFallback(data: ClientData): void {
  const json = buildPayload(data);
  const blob = new Blob([json], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = suggestedFilename(data);
  a.click();
  URL.revokeObjectURL(url);
}

// ── Write to an existing file handle ─────────────────────────────────────────

async function writeToHandle(handle: FileSystemFileHandle, data: ClientData): Promise<void> {
  const writable = await handle.createWritable();
  await writable.write(buildPayload(data));
  await writable.close();
}

// ── Main save function ───────────────────────────────────────────────────────
// handleRef: a mutable ref holding the current FileSystemFileHandle (or null).
// - First save: opens the picker, stores the handle in the ref.
// - Subsequent saves: writes directly to the existing handle.
// - On error (file moved / permissions lost): clears the ref and re-opens picker.

export async function saveSession(
  data: ClientData,
  handleRef?: React.MutableRefObject<FileSystemFileHandle | null>,
): Promise<void> {
  // Check if File System Access API is available
  if (typeof window.showSaveFilePicker !== 'function') {
    downloadFallback(data);
    return;
  }

  // If we already have a handle, try writing directly
  if (handleRef?.current) {
    try {
      await writeToHandle(handleRef.current, data);
      return;
    } catch (e) {
      // Handle lost or revoked — clear ref and fall through to picker
      handleRef.current = null;
    }
  }

  // No handle yet (first save) or handle was cleared — show the picker
  try {
    const handle = await window.showSaveFilePicker({
      suggestedName: suggestedFilename(data),
      types: [
        {
          description: 'FFM Session File',
          accept: { 'application/json': ['.json'] },
        },
      ],
    });
    await writeToHandle(handle, data);
    if (handleRef) handleRef.current = handle;
  } catch (err: any) {
    // User cancelled the picker — not an error, just do nothing
    if (err?.name === 'AbortError') return;
    // Anything else: fall back to classic download
    downloadFallback(data);
  }
}

// ── Load ─────────────────────────────────────────────────────────────────────

export function loadSession(file: File): Promise<{ data: ClientData; taxYearChanged: boolean }> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const parsed: SessionFile = JSON.parse(e.target?.result as string);
        // Merge with BLANK_CLIENT to handle missing fields from older schema versions
        const data: ClientData = { ...BLANK_CLIENT, ...parsed.data };
        // Detect tax year change
        const savedTaxYear = getTaxYearForDate(parsed.savedAt);
        const currentTaxYear = getTaxYearForDate(new Date());
        const taxYearChanged = savedTaxYear !== currentTaxYear;
        resolve({ data, taxYearChanged });
      } catch (err) {
        reject(new Error('Invalid session file'));
      }
    };
    reader.readAsText(file);
  });
}
